/**
 * The solve: one linear system inside an active-set loop.
 *
 *     M q̈ − Jᵀλ = F          Newton, once per coordinate
 *     J q̈ = 0                 the ACTIVE rows, at the acceleration level
 *     λ ≥ 0,  c ≥ 0,  c·λ = 0  complementarity, for one-sided rows
 *
 * The loop is the whole idea. Guess which constraints are doing work; solve;
 * throw out any whose force came out negative, because a rope cannot push and
 * a floor cannot suck; bring in any that are being violated; go again. It
 * settles in a handful of passes at this size.
 *
 * `J q̈ = 0` rather than `J q̈ = −J̇v` because every row's coefficients are
 * constant while the arrangement is direction-invariant — see `isExact`. Where
 * it is not, the neglected term is second order in one step and the engine
 * says it has fallen back to stepping rather than pretending otherwise.
 */
import { solveFree, solveLinear } from './linalg';
import { defOf } from './registry';
import { dot } from './vec';
import { Body, Constraint, World, Vec2 } from './types';
import { bodyIndex, isFree, segmentsOf } from './world';

export interface Solution {
    /** Body id → acceleration along each of its axes. */
    acceleration: Record<string, number[]>;
    /** Constraint id → its multiplier. Absent means inactive, which is a
     *  stronger statement than zero: the row is not doing anything at all. */
    force: Record<string, number>;
    active: string[];
    /** The arrangement does not settle to one answer. */
    singular: boolean;
    /** How many passes the active set took. Diagnostic. */
    passes: number;
    /** Rows whose value nothing decides — as against rows doing nothing. Set
     *  by the statics; the dynamics leaves it out. See `Statics.undecided`. */
    undecided?: string[];
}

interface Coord { bodyId: string; axis: number; mass: number; gravityForce: number }

const KEY = (bodyId: string, axis: number): string => `${bodyId}#${axis}`;

/**
 * The coordinates the solver works in: one per axis of every body that can
 * move. A pinned body contributes none — that is what "fixed" means, and there
 * is no second flag to fall out of step with it.
 */
function coordsOf(bodies: Body[], gravity: number): Coord[] {
    const out: Coord[] = [];
    bodies.filter(isFree).forEach(b => {
        b.axes.forEach((axis, i) => {
            // Gravity acts down; its component along this axis is what the body
            // feels. A massless body still gets a coordinate, and its row then
            // says its forces must balance — which is exactly what a massless
            // movable sheave means.
            out.push({ bodyId: b.id, axis: i, mass: b.mass, gravityForce: -b.mass * gravity * axis.y });
        });
    });
    return out;
}

export interface SolveInput {
    world: World;
    bodies: Body[];
    constraints: Constraint[];
    /** Body id → velocity along each axis. Used only to decide which rows at
     *  their limit are closing rather than opening. */
    velocity?: Record<string, number[]>;
    /** Extra forces, on top of what the parts themselves apply. Rarely needed;
     *  the solver gathers a part's own forces itself, because a caller that has
     *  to remember to do that is a caller that will one day forget — which is
     *  how a hand pulling with 19.6 N came to pull with nothing. */
    applied?: Record<string, number>;
}

const TOL = 1e-7;
/** The inertia a massless coordinate is lent, in kg. See its use below. */
export const GRAM = 1e-3;

const MAX_PASSES = 60;
/** m/s below which a body is not sliding. It is the only thing that decides
 *  whether friction is static or kinetic; there is no second rule. */
const VSTOP = 1e-7;
/** How many times the grip may be re-reckoned. Two is enough wherever the
 *  normal force does not itself depend on the friction, which is every
 *  arrangement in the syllabus. */
const MAX_GRIP = 8;

export function solve(input: SolveInput): Solution {
    const { world, bodies, constraints } = input;
    const coords = coordsOf(bodies, world.gravity);
    const index = new Map<string, number>();
    coords.forEach((c, i) => index.set(KEY(c.bodyId, c.axis), i));
    const n = coords.length;

    const rowOf = (c: Constraint): number[] => {
        const r = new Array<number>(n).fill(0);
        c.rows.forEach(x => {
            const i = index.get(KEY(x.coord.bodyId, x.coord.axis));
            if (i !== undefined) r[i] += x.coefficient;
        });
        return r;
    };
    const J = new Map<string, number[]>();
    constraints.forEach(c => J.set(c.id, rowOf(c)));

    const vel = (c: Constraint): number => {
        let s = 0;
        c.rows.forEach(x => {
            const v = input.velocity?.[x.coord.bodyId]?.[x.coord.axis] ?? 0;
            s += x.coefficient * v;
        });
        return s;
    };

    /* Gravity, plus whatever the parts apply of their own accord — a hand's
     * stated pull today, a spring later. Each is resolved onto the coordinate
     * it acts along, which is all a force is once a body has one direction. */
    const F = coords.map(c => c.gravityForce + (input.applied?.[KEY(c.bodyId, c.axis)] ?? 0));
    bodies.filter(isFree).forEach(body => {
        body.partIds.forEach(partId => {
            const part = world.parts.find(p => p.id === partId);
            if (!part) return;
            (defOf(part).forces?.(part as never, world) ?? []).forEach(f => {
                body.axes.forEach((axis, i) => {
                    const k = index.get(KEY(body.id, i));
                    if (k !== undefined) F[k] += f.vector.x * axis.x + f.vector.y * axis.y;
                });
            });
        });
    });

    /**
     * One active-set solve, given which rows may take part and which forces
     * are already known.
     *
     * `known` is the bounded rows that have run out of grip: a friction row
     * that is sliding is no longer an unknown the solver finds, it is a force
     * of a size somebody else decided. Putting it on the right-hand side is
     * the whole of "treating a clamped row as a known force".
     */
    const pass = (cands: Constraint[], known: Map<string, number>): Solution => {
        const eff = F.slice();
        known.forEach((value, id) => {
            const row = J.get(id);
            if (!row) return;
            for (let i = 0; i < n; i++) eff[i] += row[i] * value;
        });

        /* A one-sided row starts active when it is at its limit AND not
         * opening: one already at rest against the floor stays, one that has
         * just been lifted off does not, and neither has to be special-cased
         * anywhere else. A TWO-SIDED row is always active — that is what
         * two-sided means, and a table holding a block down is the reason the
         * distinction exists. */
        let active = cands
            .filter(c => !c.oneSided || (c.gap <= TOL && vel(c) <= TOL))
            .map(c => c.id);
        /** Bounded rows that have stood down because they were a second copy
         *  of a rigid row. Once out, they stay out for this solve. */
        const yielded = new Set<string>();

        let passes = 0;
        for (; passes < MAX_PASSES; passes++) {
            const rows = active.map(id => J.get(id) as number[]);
            const m = rows.length;
            const size = n + m;
            const A: number[][] = Array.from({ length: size }, () => new Array(size).fill(0));
            const b = new Array<number>(size).fill(0);

            for (let i = 0; i < n; i++) {
                /* A MASSLESS coordinate gets a gram of inertia.
                 *
                 * `m·a = ΣF` says nothing when `m` is zero, so a weightless
                 * pulley's motion is decided entirely by what it is tied to —
                 * and where that decision is ill-conditioned, the arithmetic
                 * has nothing to steady it. It happens for a real reason: a
                 * wheel slung in a bight balances only when the rope holding
                 * it pulls along the resultant of the two legs, which makes
                 * its two rows very nearly proportional. The elimination then
                 * pivots on a near-zero determinant, drives every tension to
                 * ZERO, and dumps the leftover into an acceleration nothing
                 * constrains: both weights read free fall on a bench that is
                 * plainly holding them, and the wheel read 573,175 m/s².
                 *
                 * A gram steadies it and changes no answer a reader can see.
                 * Measured on that bench, against the statics answer of
                 * 73.336 / 39.200 / 58.800 N:
                 *
                 *     0       →   0.00 /  0.00 /  0.00      wheel 5.7e5 m/s²
                 *     1e-9    →   3.72 /  1.99 /  2.99      wheel 8.5e4
                 *     1e-6    →  71.99 / 38.48 / 57.72      wheel 1.6e3
                 *     1e-3    →  73.32 / 39.19 / 58.79      wheel 1.7e1
                 *
                 * It is a regularisation and it is worth naming as one: an
                 * ideal massless wheel at exactly that alignment genuinely has
                 * no determinate acceleration, and a real one has mass. When
                 * wheels get mass of their own this goes. */
                A[i][i] = coords[i].mass > 1e-12 ? coords[i].mass : GRAM;
                b[i] = eff[i];
                for (let k = 0; k < m; k++) A[i][n + k] = -rows[k][i];
            }
            for (let k = 0; k < m; k++) {
                for (let i = 0; i < n; i++) A[n + k][i] = rows[k][i];
                b[n + k] = 0;
            }

            /* `solveFree`, not `solveLinear`: a massless body's row decides
             * nothing about its own acceleration, so a direction the ropes do
             * not pin is undetermined rather than an error, and "a body with
             * no inertia does not accelerate where nothing pushes it" is the
             * one answer always available. A genuinely INCONSISTENT system
             * still comes back null, which is the case below. */
            const x = solveFree(A, b);
            if (!x) {
                /* A bounded row never makes the bench indeterminate — it gives
                 * up its place first.
                 *
                 * A block that has slid to the end of a rough table is held by
                 * TWO things along the same direction: the stop it is against,
                 * and the grip of the table. How they share the load is
                 * genuinely undecidable and always will be. But one of them is
                 * rigid and the other is bounded, so the answer a reader can
                 * justify is that the stop takes it — which is what dropping
                 * the grip row says. Left in, the two rows are the same row
                 * twice and the whole arrangement is reported as having no
                 * answer, which is a much worse lie. */
                const rub = active.map(id => cands.find(y => y.id === id) as Constraint)
                    .findIndex(c => c.kind === 'friction');
                if (rub >= 0) {
                    yielded.add(active[rub]);
                    active = active.filter((_, k) => k !== rub);
                    continue;
                }
                return { acceleration: {}, force: {}, active, singular: true, passes };
            }

            const accel = x.slice(0, n);
            const lambda = x.slice(n);

            // A negative multiplier is a rope pushing or a floor pulling. Drop
            // the worst offender and go again — one at a time, because
            // dropping several at once can cycle. A two-sided row is never
            // dropped: its sign carries meaning, not a fault.
            let worst = -1, worstVal = -TOL;
            active.forEach((id, k) => {
                const c = cands.find(y => y.id === id) as Constraint;
                if (c.oneSided && lambda[k] < worstVal) { worstVal = lambda[k]; worst = k; }
            });
            if (worst >= 0) { active = active.filter((_, k) => k !== worst); continue; }

            /* A row at or past its limit has to join — but only if it is
             * CLOSING.
             *
             * VELOCITY WINS over acceleration. A rope that has just gone slack
             * is at its limit and its acceleration points straight back at it
             * — the weight it dropped is decelerating — but it is separating
             * at 1.8 m/s and has nothing to say for some time yet. Reading the
             * acceleration first re-activated it on the spot, which stopped a
             * weight in mid-air the instant its partner landed.
             *
             * And a row already VIOLATED is the same question, not a different
             * one. Forcing it active regardless — which is what "gap < 0 means
             * join" amounts to — pins an arrangement into the very overlap it
             * is climbing out of, and if the forces are pulling it out the row
             * is then dropped for pushing and re-added for being violated, for
             * ever. A weight dropped on top of a pulley did exactly that and
             * the whole bench came back "this does not decide what happens". */
            const toAdd = cands.find(c => {
                if (active.includes(c.id) || yielded.has(c.id)) return false;
                if (!c.oneSided) return true;
                if (c.gap > TOL) return false;
                const opening = vel(c);
                if (opening > TOL) return false;
                if (opening < -TOL) return true;
                const row = J.get(c.id) as number[];
                return row.reduce((s, v2, i) => s + v2 * accel[i], 0) < -TOL;
            });
            if (toAdd) { active.push(toAdd.id); continue; }

            const acceleration: Record<string, number[]> = {};
            bodies.filter(isFree).forEach(bd => {
                acceleration[bd.id] = bd.axes.map((_, i) => accel[index.get(KEY(bd.id, i)) as number] ?? 0);
            });
            bodies.filter(bd => !isFree(bd)).forEach(bd => { acceleration[bd.id] = []; });
            const force: Record<string, number> = {};
            active.forEach((id, k) => { force[id] = lambda[k]; });
            // A clamped row is still a force the reader is owed — it belongs
            // on the free-body diagram exactly like a multiplier.
            known.forEach((value, id) => { force[id] = value; });
            return { acceleration, force, active, singular: false, passes };
        }

        // The active set did not settle. Honest failure rather than a plausible
        // number: something about the arrangement is degenerate.
        return { acceleration: {}, force: {}, active, singular: true, passes };
    };

    /*
     * ── the grip ──────────────────────────────────────────────────────────
     *
     * A bounded row is the one thing the active set cannot decide on its own:
     * friction's limit is a fraction of ANOTHER row's multiplier, so it is not
     * known until that row has been solved. So solve, read the normal force,
     * clamp whatever has run out of grip, and go again. Two rounds settle it
     * wherever N does not itself depend on the friction — which is every
     * arrangement in the syllabus, and the loop is here for the ones that are
     * not rather than because the answer is being crept up on.
     */
    type Bounded = Constraint & { coupledTo: NonNullable<Constraint['coupledTo']> };
    const grip = constraints.filter((c): c is Bounded => c.kind === 'friction' && !!c.coupledTo);
    const plain = constraints.filter(c => !(c.kind === 'friction' && c.coupledTo));
    /** Rows that have stopped holding: a known force, not an unknown. Seeded
     *  from the motion, because a body already sliding is not gripping — which
     *  is the whole of how static and kinetic tell themselves apart. */
    const slipping = new Map<string, number>();
    grip.forEach(c => { if (Math.abs(vel(c)) > VSTOP) slipping.set(c.id, 0); });

    const rows = (): Constraint[] => [...plain, ...grip.filter(c => !slipping.has(c.id))];

    /** Re-reckon every grip against the normal force just solved for. True if
     *  anything changed its mind and the solve has to be done again. */
    const reckon = (sol: Solution): boolean => {
        let changed = false;
        grip.forEach(c => {
            const N = sol.force[c.coupledTo.constraintId] ?? 0;
            /* A FIXED grip (an infinite factor) holds whenever there is any
             * push at all and lets go the moment there is none. Written out,
             * because ∞ · 0 is NaN and every comparison against NaN is false —
             * which would have left a block that had lifted off still held. */
            const fixed = c.coupledTo.factor === Infinity;
            const limit = N <= 0 ? 0 : fixed ? Infinity : N * c.coupledTo.factor;
            const moving = vel(c);
            /* Fixed never slides, so it never joins the slipping rows: it stays
             * a held row and stops the sliding the way a bolt would. */
            if (fixed) {
                if (N <= 0 && !slipping.has(c.id)) { slipping.set(c.id, 0); changed = true; }
                if (N > 0 && slipping.has(c.id)) { slipping.delete(c.id); changed = true; }
                return;
            }
            if (Math.abs(moving) > VSTOP) {
                // Sliding: friction is as big as it gets, and it opposes the
                // motion. Nothing about the forces can change that.
                const want = -Math.sign(moving) * limit;
                if (Math.abs((slipping.get(c.id) ?? Infinity) - want) > 1e-9) {
                    slipping.set(c.id, want);
                    changed = true;
                }
            } else if (!slipping.has(c.id)) {
                /* Standing still and holding — unless holding would take more
                 * grip than there is, which is the instant it starts to slide.
                 * It keeps the sign it was already pulling with, because the
                 * body moves the way the forces were about to take it. */
                const held = sol.force[c.id] ?? 0;
                if (Math.abs(held) > limit + TOL) {
                    slipping.set(c.id, Math.sign(held) * limit);
                    changed = true;
                }
            } else {
                /* Let go at rest, and still at rest: its size is μN with THIS
                 * round's N, not the N it was let go with.
                 *
                 * That N was solved while the grip was still holding, and a
                 * slanted pull lifts a different share of the block once it
                 * slides — T falls as the far weight starts to drop, so less
                 * of the block is lifted and the floor pushes harder. Fixed at
                 * the holding N, a block hauled up-and-sideways across a rough
                 * floor got too little friction and ran away too fast. On a
                 * table or a ramp the rope pulls along the face and N never
                 * depended on it, which is why this went unseen until the
                 * floor had a grip of its own. Same sign, because which way it
                 * slides was already decided. */
                const was = slipping.get(c.id) as number;
                const want = Math.sign(was) * limit;
                if (was !== 0 && Math.abs(was - want) > 1e-9) {
                    slipping.set(c.id, want);
                    changed = true;
                }
            }
        });
        return changed;
    };

    for (let round = 0; round < MAX_GRIP; round++) {
        const sol = pass(rows(), slipping);
        if (sol.singular || !reckon(sol)) return sol;
    }
    // Whatever it settled on, solved once more so the answer matches it.
    return pass(rows(), slipping);
}

/**
 * Is this arrangement one whose accelerations stay constant between events?
 *
 * True when nothing can turn a rope while it moves, which is what makes every
 * row's coefficients constant and the motion exactly `q₀ + v₀t + ½at²`. That
 * is what lets the timeline answer "where is it at t = 1.4 s" without
 * stepping, and when it is false the engine says so rather than passing off a
 * stepped answer as an exact one.
 *
 * A body turns a run unless it can only travel ALONG it. Every axis must
 * therefore be parallel to each run it touches — or be held shut by a
 * two-sided contact, which is a block pressed onto a table: its second
 * coordinate is drawn across the run and cannot move along it, so the run
 * stays where it is. Before there were surfaces this said "everything
 * vertical", which is the same statement for a bench where everything hangs.
 */
export function isExact(
    w: World,
    bodies: Body[],
    sol: Solution,
    velocity: Record<string, number[]> = {},
): boolean {
    if (bodies.some(b => isFree(b) && b.spins)) return false;
    const byPart = bodyIndex(bodies);
    /** A body's motion in bench coordinates, from its per-axis numbers. */
    const way = (body: Body, q: number[]): Vec2 => body.axes.reduce(
        (t, axis, i) => ({ x: t.x + axis.x * (q[i] ?? 0), y: t.y + axis.y * (q[i] ?? 0) }),
        { x: 0, y: 0 } as Vec2,
    );
    return segmentsOf(w).every(seg =>
        [seg.a.partId, seg.b.partId].every(id => {
            const body = byPart.get(id);
            if (!body || !isFree(body)) return true;
            // Across the run is what turns it; along it only lengthens it.
            const turns = (u: Vec2): boolean => Math.abs(u.x * seg.dir.y - u.y * seg.dir.x) > 1e-9;
            return !turns(way(body, sol.acceleration[body.id] ?? []))
                && !turns(way(body, velocity[body.id] ?? []));
        }));
}
